import React from "react";
import { Leaf, Paintbrush, Wind, ShieldCheck, Recycle, Trash2, Check } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function SustainabilityHealth() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const benefitsLowVoc = ["Minimizes harmful fumes and odors during application", "Safe for families, children, and pets immediately after drying", "Improves long-term indoor air quality", "Reduces risk of respiratory irritation and allergic reactions"];
  const benefitsDisposal = ["Strict adherence to local environmental waste regulations", "Responsible recycling of leftover paint cans and materials", "Safe containment and removal of lead-based paint chips", "Minimizing landfill impact through conscious material usage"];
  return <section id="sustainability-health" ref={ref} className={cn("relative w-full py-24 overflow-hidden bg-background transition-opacity duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-[var(--chart-2)]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-[var(--chart-2)]/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {}
        <div className="flex flex-col items-center text-center mb-16 max-w-3xl mx-auto">
          <div className="relative mb-6 inline-flex items-center justify-center">
            <div className="relative z-10 bg-background p-4 rounded-full shadow-sm border border-[var(--chart-2)]/20">
              <Paintbrush className="w-10 h-10 text-[var(--chart-2)]" strokeWidth={1.5} />
            </div>
            <div className="absolute -right-2 -bottom-2 z-20 bg-[var(--chart-2)] text-white p-2 rounded-full shadow-md border-2 border-background">
              <Leaf className="w-5 h-5" strokeWidth={2.5} />
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
            Sustainability & <span className="text-[var(--chart-2)]">Health</span>
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            At ANV Painting, we believe a beautiful finish shouldn't come at the cost of your health or the environment. 
            We prioritize eco-friendly products and responsible practices to ensure your space is safe, vibrant, and sustainable.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {}
          <Card className="border-[var(--chart-2)]/20 bg-[var(--chart-2)]/5 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-[var(--chart-2)]/10 text-[var(--chart-2)]">
                  <Wind className="w-8 h-8" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-foreground">Low-VOC Technology</CardTitle>
                  <CardDescription className="text-[var(--chart-2)] font-medium mt-1">
                    Breathe Easier, Live Better
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Volatile Organic Compounds (VOCs) are solvents released into the air as paint dries. 
                We exclusively use premium, low-VOC and zero-VOC paints that deliver exceptional durability without the harsh chemical emissions.
              </p>
              <ul className="space-y-4">
                {benefitsLowVoc.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <Leaf className="w-5 h-5 text-[var(--chart-2)] shrink-0 mt-1" />
                    <span className="text-foreground/90">{item}</span>
                  </li>)}
              </ul>
            </CardContent>
          </Card>

          {}
          <Card className="border-[var(--chart-2)]/20 bg-[var(--chart-2)]/5 shadow-sm hover:shadow-md transition-shadow duration-300">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-lg bg-[var(--chart-2)]/10 text-[var(--chart-2)]">
                  <Recycle className="w-8 h-8" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-foreground">Eco-Friendly Disposal</CardTitle>
                  <CardDescription className="text-[var(--chart-2)] font-medium mt-1">
                    Responsible Waste Management
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Our commitment to the environment extends beyond the paint we apply. 
                We implement strict waste management protocols to ensure that all materials are disposed of or recycled responsibly, keeping North Queensland beautiful.
              </p>
              <ul className="space-y-4">
                {benefitsDisposal.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <Leaf className="w-5 h-5 text-[var(--chart-2)] shrink-0 mt-1" />
                    <span className="text-foreground/90">{item}</span>
                  </li>)}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}